/**
 * Sport facility types supported by ToAfrica (aligned with API `pitch_type` values).
 */
export const FACILITY_TYPE_OPTIONS = [
  { value: "football", label: "Football", icon: "fa-solid fa-futbol" },
  { value: "basketball", label: "Basketball", icon: "fa-solid fa-basketball" },
  { value: "volleyball", label: "Volleyball", icon: "fa-solid fa-volleyball" },
  { value: "cricket", label: "Cricket", icon: "fa-solid fa-baseball-bat-ball" },
  { value: "tennis", label: "Tennis", icon: "fa-solid fa-table-tennis-paddle-ball" },
  { value: "rugby", label: "Rugby", icon: "fa-solid fa-football" },
  { value: "multi-purpose", label: "Multi-purpose", icon: "fa-solid fa-dumbbell" },
];

const TYPE_BY_VALUE = Object.fromEntries(
  FACILITY_TYPE_OPTIONS.map((t) => [t.value, t])
);

/** @param {string | null | undefined} type */
export function normalizeFacilityType(type) {
  if (!type) return "";
  return String(type).trim().toLowerCase().replace(/\s+/g, "-");
}

/** @param {string | null | undefined} type */
export function formatFacilityType(type) {
  const key = normalizeFacilityType(type);
  if (TYPE_BY_VALUE[key]) return TYPE_BY_VALUE[key].label;
  if (!type) return "Sports Facility";
  return String(type)
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/** @param {string | null | undefined} type */
export function facilityTypeIcon(type) {
  const key = normalizeFacilityType(type);
  return TYPE_BY_VALUE[key]?.icon || "fa-solid fa-building";
}

/** @param {string | null | undefined} type */
export function facilityTypeNoun(type) {
  const key = normalizeFacilityType(type);
  const nouns = {
    football: "pitch",
    basketball: "court",
    volleyball: "court",
    cricket: "ground",
    tennis: "court",
    rugby: "pitch",
    "multi-purpose": "facility",
  };
  return nouns[key] || "facility";
}

/** Match Hub sport slug (football, basketball, …). */
export function normalizeMatchSport(sport) {
  return normalizeFacilityType(sport);
}

/**
 * Whether a facility pitch_type from the API can be booked on that sport's booking route.
 * Mirrors backend checks in application/routes/booking (per-sport bookings modules).
 *
 * @param {string | null | undefined} pitchType
 * @param {string | null | undefined} sportSlug
 */
export function facilityAcceptsBookingForSport(pitchType, sportSlug) {
  const pt = normalizeFacilityType(pitchType);
  const sport = normalizeMatchSport(sportSlug);
  if (!sport) return false;
  if (!pt) return false;

  switch (sport) {
    case "football":
      return pt.includes("football");
    case "basketball":
    case "volleyball":
    case "tennis":
    case "rugby":
    case "cricket":
      return pt === sport;
    default:
      return pt === sport;
  }
}

/**
 * Resolved pitch_type for match payloads (never invent a sport that conflicts with the facility).
 *
 * @param {string | null | undefined} pitchType
 * @param {string | null | undefined} sportSlug
 */
export function resolvePitchTypeForSport(pitchType, sportSlug) {
  const pt = normalizeFacilityType(pitchType);
  if (pt && facilityAcceptsBookingForSport(pt, sportSlug)) return pt;
  return normalizeMatchSport(sportSlug) || "";
}
